// eslint-disable-next-line import/no-cycle
import AdvisorProxyApi from '@advisor/advisorProxyApi';
import RwAdvisorService from '@advisor/rwSdk/AdvisorService';
// eslint-disable-next-line import/no-cycle
import Settings from '@advisor/models/store/settings';
// TODO: remove this line once DB changes have been made in prod - this code should not live past March 2020
import tempIconConverter from '@advisor/util/tempIconConverter';
import {PLATFORM_AREA_MAP, ACCESS_LEVEL, RIA_GROUP_ID, COMPONENT_ID} from '@advisor/util/constants';
import {returnNoneApi} from '@advisor/util/returnNoneApi';
import exceptionHandler from '@advisor/util/exceptionHandler';
import EnterpriseService from '@advisor/rwSdk/EnterpriseService';
import getRiaSettingsForEnv from '@advisor/config/riaSettings';

const stateObj = function () {
  // Load RIA settings from env-specific config (replaces getRiaFirm + getRiaPrivateLabelSetting API calls)
  const settings = new Settings();
  const envConfig = getRiaSettingsForEnv();

  settings.ria.companyLegalName = envConfig.companyLegalName;
  settings.ria.website = envConfig.website;
  settings.ria.contactPage = envConfig.contactPageLink;
  settings.ria.loginBackground = envConfig.riaBgImage;
  settings.ria.squareLogo = envConfig.riaSquareLogo;
  settings.ria.rectangleLogo = envConfig.riaRectangleLogo;
  settings.ria.favicon = envConfig.riaFavicon;
  settings.ria.headerColor = envConfig.riaHeaderColor;
  settings.ria.fontSize = envConfig.riaFontSize;
  settings.ria.fontFamily = envConfig.riaFontFamily;
  settings.ria.sidebarColor = envConfig.riaSidebarColor;
  settings.ria.sidebarFontColor = envConfig.riaSidebarFontColor;
  settings.ria.headerFontColor = envConfig.riaHeaderFontColor;
  settings.ria.headerHoverColor = envConfig.riaHeaderHoverColor;
  settings.ria.disclaimer = '';

  return settings;
};

const getters = {
  riaCompanyLegalName: (state) => state.ria.companyLegalName,
  riaLoginBackgroundStyle: (state) =>
    state.ria.loginBackground
      ? {
          'background-image': `url('${process.env.VUE_APP_BUCKET_URL}${state.ria.loginBackground}')`,
          'background-size': `cover`,
        }
      : {},
  riaLoginLogoLink: (state) => {
    const {website} = state.ria;

    return website === 'none' || website === 'None' || website === '' ? '' : website;
  },
  riaLoginLogoPath: (state) => (state.ria.squareLogo ? `${process.env.VUE_APP_BUCKET_URL}${state.ria.squareLogo}` : null),
};

const actions = {
  setRiaComponent({commit}, payload) {
    commit('SET_RIA_COMPONENT', payload);
  },
  setFooter({commit}, payload) {
    commit('SET_FOOTER', payload);
  },
  // eslint-disable-next-line max-lines-per-function
  async getPlatformAccess({commit, rootState}) {
    let platformAccessList;

    try {
      // Platform Access
      const riaUserId = rootState.user.id;

      platformAccessList = await RwAdvisorService.getRiaRoleAccessComponents({
        riaUserId,
      }).catch((error) => {
        throw error;
      });

      platformAccessList.forEach((item) => {
        commit('SET_PLATFORM_ACCESS', item);
      });

      // Side Bar Menu
      let sideBarMenu = {};
      const parentMenu = [];

      const sideBarlist = platformAccessList.filter(
        (item) => item.isSidebarMenu === true && item.riaGroupsAccessLevelsId !== ACCESS_LEVEL.NO_ACCESS
      );

      sideBarlist.forEach((item) => {
        // TODO: remove this line once DB changes have been made in prod - this code should not live past March 2020
        // eslint-disable-next-line no-param-reassign
        item.icon = tempIconConverter.convertAdvisorNavIconsToMdi(item.icon);

        if (Number.isNaN(item.fkRiaComponentid) || item.fkRiaComponentid === null) {
          parentMenu[item.riaComponentId] = item;
          parentMenu[item.riaComponentId].children = [];
        } else if (
          typeof parentMenu[item.fkRiaComponentid] !== 'undefined' &&
          parentMenu[item.fkRiaComponentid].riaComponentId === item.fkRiaComponentid
        ) {
          if (item.riaComponentId === COMPONENT_ID.CLIENT_AUTH_BANK_LINKING) {
            parentMenu[item.fkRiaComponentid].children.push(item);

            return;
          }

          parentMenu[item.fkRiaComponentid].children.push(item);
        }
      });

      Object.assign(sideBarMenu, parentMenu);

      sideBarMenu = _.orderBy(sideBarMenu, ['displayOrder'], ['asc']);

      commit('SET_SIDEBAR', sideBarMenu);
    } catch (error) {
      exceptionHandler.handleGenericException('settings.getPlatformAccess', error);
    }

    return platformAccessList;
  },
  setQuickAccessButton({commit}, payload) {
    commit('SET_QUICK_ACCESS_BUTTON', payload);
  },
  async getAdvisorFilter({commit, rootState}) {
    try {
      const riaGroupId = rootState?.user?.profile?.riaGroupId || RIA_GROUP_ID.NO_ACCESS;

      const payload = await RwAdvisorService.getAdvisorFilters({
        filterGroupId: riaGroupId,
      }).catch((error) => {
        const newErr = {
          ...error,
          appMessage: 'Possible errors: 1. no user, 2. no user profile, 3. no RIA group ID in user profile.',
        };

        throw newErr;
      });

      commit('SET_FILTER', payload);
    } catch (error) {
      exceptionHandler.handleGenericException('settings.getAdvisorFilter', error);
    }
  },
  // eslint-disable-next-line no-unused-vars, require-await, @typescript-eslint/no-unused-vars
  async getRiaSettings({commit}, {riaId}) {
    try {
      // Replaced API calls with env-specific hardcoded config:
      //   GET /serviceToken/v1/advisor/riaLabelSettings/{riaId}
      //   GET /serviceToken/v1/advisor/riaFirm/{riaId}
      //   GET /serviceToken/v1/advisor/riaFirm/riaUrl (removed - redundant)
      const riaSettingsData = getRiaSettingsForEnv();

      commit('SET_RIA_SETTINGS', riaSettingsData);

      return riaSettingsData;
    } catch (error) {
      return exceptionHandler.handleGenericException('settings.getRiaSettings', error);
    }
  },
  async getEnterpriseFeatures({commit, state}, payload) {
    try {
      let enterpriseId;

      if (payload?.enterpriseId) {
        enterpriseId = payload.enterpriseId;
        state.enterprise.enterpriseId = enterpriseId;
      }

      const result = await EnterpriseService.getEnterpriseFeatures({
        enterpriseId,
      });

      const enterpriseFeatures = result.map((row) => {
        const feature = {feature: row.featureName};

        return feature;
      });

      await commit('SET_ENTERPRISE_FEATURES', enterpriseFeatures);
    } catch (error) {
      exceptionHandler.handleGenericException('settings.getEnterpriseFeatures', error);
    } finally {
      // eslint-disable-next-line no-unused-vars
      const featureCalls = state.enterprise.features.reduce((features) => features, []);

      if (featureCalls.length > 0) {
        await Promise.all(featureCalls);
      }
    }
  },
  setDrawer({commit}, payload) {
    commit('SET_DRAWER', payload);
  },
  setActiveSection({commit}, payload) {
    commit('SET_ACTIVESECTION', payload);
  },
  dialogOpen({commit}, payload) {
    commit('SET_DIALOG', payload);
  },
  setMiniDrawer({commit}, payload) {
    commit('SET_MINIDRAWER', payload);
  },
  setMobile({commit}, payload) {
    commit('SET_MOBILE', payload);
  },
  async savePrivateLabelData({commit}, payload) {
    let response = {};

    if (typeof payload.isTesting === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      payload.isTesting = false;
    }

    try {
      if (payload.isTesting === false) {
        response = await AdvisorProxyApi.setPrivateLabelData(payload);
        returnNoneApi(response);
      }

      commit('UPDATE_PRIVATE_LABEL_DATA', payload);

      return response;
    } catch (error) {
      exceptionHandler.handleGenericException('settings.savePrivateLabelData', error);

      return Promise.reject(error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async uploadFileToS3({commit}, payload) {
    try {
      const response = await AdvisorProxyApi.uploadFileToS3(payload);

      returnNoneApi(response);

      return response;
    } catch (error) {
      exceptionHandler.handleGenericException('settings.uploadFileToS3', error);

      return Promise.reject(error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getFileFromS3({commit}, payload) {
    try {
      const response = await AdvisorProxyApi.getFileFromS3(payload);

      if (response.status !== 200) {
        throw new Error(response.status);
      }

      return response;
    } catch (error) {
      exceptionHandler.handleGenericException('settings.getFileFromS3', error);

      return Promise.reject(error);
    }
  },
};

const mutations = {
  SET_RIA_SETTINGS(state, payload) {
    state.ria.loadData(payload);
  },
  SET_RIA_COMPONENT(state, payload) {
    state.default.riaComponent = payload.riaComponent;
  },
  SET_FOOTER(state, payload) {
    state.ria.website = state.ria.formatLink(payload.website);
    state.ria.contactPage = state.ria.formatLink(payload.contactPageLink);
  },
  SET_QUICK_ACCESS_BUTTON(state, payload) {
    state.default.quickAccessButton = payload;
  },
  SET_SIDEBAR(state, payload) {
    state.default.sideBarMenu = payload;
  },
  SET_MINIDRAWER(state, payload) {
    state.default.miniDrawer = payload;
  },
  SET_FILTER(state, payload) {
    state.default.advisorFilter = payload;
  },
  SET_ENTERPRISE_FEATURES(state, payload) {
    state.enterprise.features = payload.map((row) => row.feature);
    state.enterprise.featuresLoaded = true;
  },
  SET_DRAWER(state, payload) {
    state.default.showDrawer = payload;
  },
  SET_MOBILE(state, payload) {
    state.default = {...state.default, mobile: payload};
  },
  SET_ACTIVESECTION(state, payload) {
    state.default.activeQuickAccessButton = payload;
  },
  SET_DIALOG(state, payload) {
    state.default.activeQuickAccessButton = payload;
  },
  SET_PLATFORM_ACCESS(state, payload) {
    const platformArea = PLATFORM_AREA_MAP[payload.platformArea] || payload.platformArea;

    state.default.platformAccess[platformArea] = payload.riaGroupsAccessLevelsId;
  },
  UPDATE_PRIVATE_LABEL_DATA(state, payload) {
    const propertyMap = Object.freeze({
      riaBgImage: 'loginBackground',
      riaButtonType: 'buttonType',
      riaDisclaimer: 'disclaimer',
      riaEmailDisclaimer: 'emailDisclaimer',
      riaFavicon: 'favicon',
      riaFontFamily: 'fontFamily',
      riaFontSize: 'fontSize',
      riaHeaderColor: 'headerColor',
      riaHeaderFontColor: 'headerFontColor',
      riaHeaderHoverColor: 'headerHoverColor',
      riaRectangleLogo: 'rectangleLogo',
      riaSidebarColor: 'sidebarColor',
      riaSidebarFontColor: 'sidebarFontColor',
      riaSidebarHoverColor: 'sidebarHoverColor',
      riaSidebarStyle: 'sidebarStyle',
      riaSquareLogo: 'squareLogo',
      rtqAnswerText: 'rtqGaugeText',
      totalAccountsCashLogo: 'accountsTotalCashValueImage',
      totalAccountsValueLogo: 'accountsTotalPortfolioValueImage',
      totalCashValueLogo: 'totalCashValueImage',
      totalGoalsCashLogo: 'goalsTotalCashValueImage',
      totalGoalsValueLogo: 'goalsTotalPortfolioValueImage',
      totalPortfolioValueLogo: 'totalPortfolioValueImage',
      wealthProjectionDisclaimer: 'wealthProjectionDisclaimer',
    });
    const keys = Object.keys(payload);

    keys.forEach((key) => {
      const value = propertyMap[key];

      if (value) {
        state.ria[value] = payload[key];
      }
    });
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters,
  actions,
  mutations,
};
